import { log } from '@/log'
import { ApiClient } from '@ajax/modules/services/client'
import { HlAppViewModelBuilder } from '@ajax/vue/components/organisms/external-payment/model/hl-app-view-model'
import { DefaultViewModelBuilder, ViewModel } from '@ajax/vue/components/organisms/external-payment/model/view-model'
import { Either, left, right } from 'fp-ts/lib/Either'

type CallValidateApiFunction = (
  apiPathBase: string,
  encodedQueries: string,
  postData: any
) => Promise<Either<Error, ValidateApiResponse>>

type ConvertValidateApiResponseToViewModelFunction = (
  response: ValidateApiResponse
) => ViewModel

export interface ExternalPaymentInteraction {
  callValidateApi: CallValidateApiFunction,
  convertValidateApiResponseToViewModel: ConvertValidateApiResponseToViewModelFunction,
}

export function createExternalPaymentInteraction(
  context: {
    forHlApp: boolean,
  }
): ExternalPaymentInteraction {
  return context.forHlApp
    ? {
      callValidateApi,
      convertValidateApiResponseToViewModel: convertValidateApiResponseToViewModelForHlApp,
    }
    : {
      callValidateApi,
      convertValidateApiResponseToViewModel: convertValidateApiResponseToViewModelDefault,
    }
}

export interface ValidateApiResponse {
  is_valid_request: boolean
  order_complete_path?: string
}

const callValidateApi: CallValidateApiFunction = async (
  apiPathBase: string,
  encodedQueries: string,
  postData: any
): Promise<Either<Error, ValidateApiResponse>> => {
  try {
    // ApiClient.postではクエリを設定できないため、エンドポイントに直接付加する
    const response = await ApiClient
      .post(`${apiPathBase}/validate?${encodedQueries}`)
      .send(postData)

    log.debug({ response })

    return right(response.body.data)
  } catch (error) {
    return left(error as Error)
  }
}

const convertValidateApiResponseToViewModelDefault: ConvertValidateApiResponseToViewModelFunction = (
  response: ValidateApiResponse
): ViewModel => {
  // 決済は成功した可能性があるがリダイレクト時のパラメータが不正であるケース
  if (!response.is_valid_request) {
    log.debug('Cannot show order complete page because the request params are invalid.')

    return DefaultViewModelBuilder.forUnexpectedError()
      .withCheckOrderHistoryAlertSection()
      .buildWithOrderHistoryLink()
  }

  // 他のアカウントでログインしているなどの理由で注文完了画面が表示できないケース
  if (!response.order_complete_path) {
    log.debug('Cannot show order complete page because the order information is not be obtained.')

    return DefaultViewModelBuilder.forPaymentSuccessfullyDone()
      .addCheckOrderHistoryErrorMessage()
      .addGuideToOrderHistoryErrorMessage()
      .withCheckOrderHistoryAlertSection()
      .buildWithOrderHistoryLink()
  }

  // 正常なリクエスト
  // 注文完了画面を表示できる
  return DefaultViewModelBuilder.forPaymentSuccessfullyDone()
    .addGuideToOrderCompleteInfoMessage()
    .buildWithOrderCompleteLink(response.order_complete_path)
}

const convertValidateApiResponseToViewModelForHlApp: ConvertValidateApiResponseToViewModelFunction = (
  response: ValidateApiResponse
): ViewModel => {
  // 決済は成功した可能性があるがリダイレクト時のパラメータが不正であるケース
  if (!response.is_valid_request) {
    log.debug('Cannot show order complete page because the request params are invalid.')

    return HlAppViewModelBuilder.forUnexpectedError()
      .withCheckOrderHistoryAlertSection()
      .buildWithHlAppOrderHistory()
  }

  // 他のアカウントでログインしているなどの理由で注文完了画面が表示できないケース
  if (!response.order_complete_path) {
    log.debug('Cannot show order complete page because the order information is not be obtained.')

    return HlAppViewModelBuilder.forPaymentSuccessfullyDone()
      .addCheckOrderHistoryErrorMessage()
      .addGuideToOrderHistoryErrorMessage()
      .withCheckOrderHistoryAlertSection()
      .buildWithHlAppOrderHistory()
  }

  // 正常なリクエスト
  // 注文完了画面を表示できる
  return HlAppViewModelBuilder.forPaymentSuccessfullyDone()
    .buildWithHlAppOrderComplete()
}
